import React, { useRef, useState } from 'react';
import { Form, Button, Alert, Tab, Nav } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import useDocs from '../../hooks/useDocs';
import '../../styles/UpdateProfile.css';

function ProfileTabNav() {
  return (
    <Nav variant="pills" className="update-profile-tab-nav">
      <Nav.Item>
        <Nav.Link eventKey="first">Public</Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link eventKey="second">Personal</Nav.Link>
      </Nav.Item>
    </Nav>
  );
}

export default function UpdateProfile() {
  const emailRef = useRef();
  const passwordRef = useRef();
  const passwordConfirmRef = useRef();
  const { currentUser, updatePassword, updateEmail, updateProfile } = useAuth();
  const [error, setError] = useState('');
  const [pubError, setPubError] = useState('');
  const [success, setSuccess] = useState('');
  const [pubSuccess, setPubSuccess] = useState('');
  const [updated, setUpdated] = useState(false);
  const { docs, loading: profileLoading } = useDocs('profiles', updated);
  const [loading, setLoading] = useState(true);
  const [pubLoading, setPubLoading] = useState(true);

  function handleChange() {
    setLoading(false);
  }

  function handlePubChange() {
    setPubLoading(false);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const promises = [];
    setLoading(true);
    setError('');
    setSuccess('');

    if (emailRef.current.value !== currentUser.email) {
      promises.push(updateEmail(emailRef.current.value));
    }
    if (passwordRef.current.value) {
      promises.push(updatePassword(passwordRef.current.value));
    }
    Promise.all(promises)
      .then(() => {
        setSuccess('Successfully updated');
      })
      .catch(() => {
        setError('Failed to update account');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  async function handlePublic(e) {
    e.preventDefault();
    const f = e.target;
    setPubLoading(true);
    setPubError('');
    setPubSuccess('');
    try {
      await updateProfile(
        f.elements.publicName.value,
        f.elements.publicBio.value,
        f.elements.twitterUrl.value,
        f.elements.instagramUrl.value,
        f.elements.facebookUrl.value,
        f.elements.profilePic.files[0],
        f.elements.profileHeader.files[0]
      );
      setPubSuccess('Successfully updated');
      setUpdated(!updated);
    } catch (err) {
      console.error(err);
      if (err.textSaved) {
        setPubSuccess('Name and biography updated successfully.');
        setPubError(
          err.imageErrors?.join(' ') ||
            'Photos could not be uploaded. Firebase Storage requires upgrading your project to the Blaze plan.'
        );
        setUpdated(!updated);
      } else {
        setPubError(err.message || 'Failed to update account');
      }
    } finally {
      setPubLoading(false);
    }
  }

  return (
    <div className="update-profile-page">
      <Tab.Container id="update-profile-tabs" defaultActiveKey="first">
        <div className="update-profile-layout">
          <main className="update-profile-main">
            <div className="update-profile-rail-mobile d-md-none">
              <ProfileTabNav />
            </div>

            <Tab.Content>
              <Tab.Pane eventKey="first">
                <div className="update-profile-card">
                  <h2 className="update-profile-title">Update Profile</h2>
                  {pubError && <Alert variant="danger">{pubError}</Alert>}
                  {pubSuccess && (
                    <Alert variant="success">
                      {pubSuccess}
                      {docs.username && (
                        <Link className="ml-2" to={`/user/${docs.username}`}>
                          View changes
                        </Link>
                      )}
                    </Alert>
                  )}
                  <Form
                    id="updatePublic"
                    key={`public-${updated}-${profileLoading ? 'l' : 'r'}`}
                    onChange={handlePubChange}
                    onSubmit={handlePublic}
                  >
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        name="publicName"
                        type="text"
                        defaultValue={docs.name || ''}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Biography</Form.Label>
                      <Form.Control
                        name="publicBio"
                        type="text"
                        defaultValue={docs.bio || ''}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Twitter / X</Form.Label>
                      <Form.Control
                        name="twitterUrl"
                        type="url"
                        inputMode="url"
                        placeholder="https://x.com/yourhandle"
                        defaultValue={docs.twitterUrl || ''}
                      />
                      <Form.Text className="text-muted">
                        Full profile link (https://…)
                      </Form.Text>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Instagram</Form.Label>
                      <Form.Control
                        name="instagramUrl"
                        type="url"
                        inputMode="url"
                        placeholder="https://www.instagram.com/yourhandle"
                        defaultValue={docs.instagramUrl || ''}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Facebook</Form.Label>
                      <Form.Control
                        name="facebookUrl"
                        type="url"
                        inputMode="url"
                        placeholder="https://www.facebook.com/your.profile"
                        defaultValue={docs.facebookUrl || ''}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Profile picture</Form.Label>
                      <Form.Control
                        name="profilePic"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                      />
                      {docs.img && (
                        <img
                          className="update-profile-preview img-fluid w-75"
                          src={docs.img}
                          alt="Profile"
                        />
                      )}
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Profile Header</Form.Label>
                      <Form.Control
                        name="profileHeader"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                      />
                      <Form.Text>
                        Photo uploads need Firebase Blaze plan. Name and bio save
                        without photos.
                      </Form.Text>
                      {docs.header && (
                        <img
                          className="update-profile-preview update-profile-preview--header img-fluid w-75"
                          src={docs.header}
                          alt="Banner"
                        />
                      )}
                    </Form.Group>
                    <Button disabled={pubLoading} className="w-100" type="submit">
                      Update
                    </Button>
                  </Form>
                </div>
              </Tab.Pane>

              <Tab.Pane eventKey="second">
                <div className="update-profile-card">
                  <h2 className="update-profile-title">Update Personal</h2>
                  {error && <Alert variant="danger">{error}</Alert>}
                  {success && <Alert variant="success">{success}</Alert>}
                  <Form onChange={handleChange} onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        ref={emailRef}
                        required
                        defaultValue={currentUser.email}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        ref={passwordRef}
                        placeholder="Leave blank to keep the same"
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Password Confirmation</Form.Label>
                      <Form.Control
                        type="password"
                        ref={passwordConfirmRef}
                        placeholder="Leave blank to keep the same"
                      />
                    </Form.Group>
                    <Button disabled={loading} className="w-100" type="submit">
                      Update
                    </Button>
                  </Form>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </main>

          <aside className="update-profile-rail d-none d-md-block">
            <ProfileTabNav />
          </aside>
        </div>
      </Tab.Container>
    </div>
  );
}
